import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Link,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Controller,
  FormState,
  useForm,
  ValidationRule,
} from 'react-hook-form';
import validationUtils from '../../services/validationUtils';
import TextInput2 from '../form/TextInput2';
import PasswordConstraintChecker from '../form/PasswordConstraintChecker';

const { __environment__ } = window as any;
const termsUrl = __environment__.REACT_APP_PROVIDER_EULA_URL.trim();

const PREFIX = 'OnboardProvider';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  password: `${PREFIX}-password`,
  terms: `${PREFIX}-terms`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: 305,
    padding: 32,
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.terms}`]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
}));

interface OnboardProviderFormProps {
  email?: string;
  submit: (info: any) => void;
}

export default function OnboardProviderForm({
  email,
  submit,
}: OnboardProviderFormProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const { control, formState, handleSubmit, watch, getValues } = useForm({
    defaultValues: {
      Email: email || '',
      Password: '',
      ConfirmPassword: '',
    } as any,
  });
  const currentPassword = watch('Password');
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <Root className={classes.container}>
      <Typography className={classes.title}>
        {t('onboard.createYourAccount')}
      </Typography>
      <TextInput2
        label={t('onboard.email')}
        name={'Email'}
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: requiredFieldMessage,
          pattern: validationUtils.emailValidation(),
        }}
      />
      <FormControl variant="outlined" className={classes.password}>
        <InputLabel htmlFor="password" required={true}>
          {t('onboard.password')}
        </InputLabel>
        <Controller
          name={'Password'}
          control={control}
          defaultValue={''}
          rules={{
            required: requiredFieldMessage,
            validate: validationUtils.validatePassword(t),
          }}
          render={({ field }) => (
            <OutlinedInput
              {...field}
              id="password"
              type={passwordVisible ? 'text' : 'password'}
              required={true}
              error={formState.errors ? !!formState.errors['Password'] : false}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    onMouseDown={(evt) => evt.preventDefault()}
                    edge="end">
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label={t('onboard.password')}
            />
          )}
        />
      </FormControl>
      <PasswordConstraintChecker currentValue={currentPassword} />
      <TextInput2
        className={classes.password}
        label={t('onboard.confirmPassword')}
        name={'ConfirmPassword'}
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: requiredFieldMessage,
          validate: validationUtils.validatePasswordMatch(getValues, t),
        }}
        type="password"
      />
      <Box className={classes.terms}>
        <Typography variant="body2" align="center">
          {t('onboard.agree')}
        </Typography>
        <Link
          href={termsUrl}
          target="_blank"
          color="primary"
          underline="hover"
          align="center">
          {t('onboard.terms')}
        </Link>
      </Box>
      <Button
        color="primary"
        variant="contained"
        onClick={handleSubmit(submit)}>
        {t('onboard.createAccount')}
      </Button>
    </Root>
  );
}
